import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { GraduationCap, MapPin } from 'lucide-react';
import type { Court } from '@court-sync/shared';
import { Button, Checkbox, FieldLabel } from '@/components/ui';
import { DashboardFilterCard } from '@/components/dashboard/DashboardSectionCard';
import {
  DashboardEmptyStateCard,
  DashboardEmptyStateIcon,
} from '@/components/dashboard/DashboardEmptyState';
import { clubService } from '@/services/club';

interface ClubAcademyAccessSettingsTabProps {
  clubId: string;
}

interface AcademyAccessRow {
  academyId: string;
  academyName: string;
  courtIds: string[];
}

export function ClubAcademyAccessSettingsTab({ clubId }: ClubAcademyAccessSettingsTabProps) {
  const [courts, setCourts] = useState<Court[]>([]);
  const [rows, setRows] = useState<AcademyAccessRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingAcademyId, setSavingAcademyId] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [courtsRes, accessRes] = await Promise.all([
        clubService.getClubCourts(clubId),
        clubService.getAcademyCourtAccess(clubId),
      ]);
      setCourts(courtsRes.success && courtsRes.data ? courtsRes.data : []);
      setRows(accessRes.success && accessRes.data ? accessRes.data : []);
    } finally {
      setLoading(false);
    }
  }, [clubId]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const toggleCourt = (academyId: string, courtId: string, checked: boolean) => {
    setRows((prev) =>
      prev.map((row) => {
        if (row.academyId !== academyId) return row;
        return {
          ...row,
          courtIds: checked
            ? [...row.courtIds, courtId]
            : row.courtIds.filter((id) => id !== courtId),
        };
      })
    );
  };

  const saveRow = async (row: AcademyAccessRow) => {
    setSavingAcademyId(row.academyId);
    try {
      const res = await clubService.updateAcademyCourtAssignments(
        clubId,
        row.academyId,
        row.courtIds
      );
      if (res.success) {
        toast.success(`Court access updated for ${row.academyName}`);
      } else {
        toast.error(res.error || 'Failed to update court access');
      }
    } finally {
      setSavingAcademyId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[120px] items-center justify-center" aria-busy="true">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900 dark:border-white" />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <DashboardEmptyStateCard>
        <DashboardEmptyStateIcon icon={GraduationCap} />
        <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">
          No linked academies
        </h3>
        <p className="mb-4 text-gray-500 dark:text-gray-400">
          Academy court assignments appear here when an academy shares the same organization as
          this club.
        </p>
      </DashboardEmptyStateCard>
    );
  }

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        Courts belong to the club. Assign courts to each academy so they can schedule lessons on
        those courts only — unassigned courts are not available in the academy calendar.
      </p>

      {courts.length === 0 ? (
        <DashboardEmptyStateCard>
          <DashboardEmptyStateIcon icon={MapPin} />
          <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">
            No courts yet
          </h3>
          <p className="mb-4 text-gray-500 dark:text-gray-400">
            Add courts to this club before assigning academy access.
          </p>
          <Link to="/admin/courts">
            <Button type="button">Manage courts</Button>
          </Link>
        </DashboardEmptyStateCard>
      ) : null}

      {rows.map((row) => (
        <DashboardFilterCard
          key={row.academyId}
          title={row.academyName}
          description="Select the club courts this academy may schedule lessons on."
        >
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {courts.map((court) => {
              const inputId = `${row.academyId}-${court.id}`;
              const checked = row.courtIds.includes(court.id);
              return (
                <div key={court.id} className="flex items-center gap-3">
                  <Checkbox
                    id={inputId}
                    checked={checked}
                    onCheckedChange={(value) =>
                      toggleCourt(row.academyId, court.id, value === true)
                    }
                  />
                  <FieldLabel htmlFor={inputId} className="mb-0 font-normal cursor-pointer">
                    {court.name}
                  </FieldLabel>
                </div>
              );
            })}
          </div>
          <div className="mt-6 flex justify-end border-t border-border pt-4">
            <Button
              type="button"
              size="sm"
              isLoading={savingAcademyId === row.academyId}
              onClick={() => void saveRow(row)}
            >
              Save assignments
            </Button>
          </div>
        </DashboardFilterCard>
      ))}
    </div>
  );
}
